import { useState, useEffect, useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'
import { GET_AD_ASSETS, GET_AD_ASSET_BY_ID } from '@/graphql/ads/ad-assets-queries'
import getCroppedImg from '@/utils/crop-image'
import { constructProxyUrl } from '@/utils/proxy'

// Utility function to detect media type
const getMediaType = (fileName) => {
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm']
    const lowerName = fileName.toLowerCase()
    return videoExtensions.some((ext) => lowerName.includes(ext)) ? 'video' : 'image'
}

export const useAdAssets = (campaignId, selectFormatData = {}, assetId) => {
    const [assetData, setAssetData] = useState(null)
    const [croppedImageUrl, setCroppedImageUrl] = useState(null)
    const [carouselAssets, setCarouselAssets] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const [fetchCampaignAssets] = useLazyQuery(GET_AD_ASSETS, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const assets = data?.AdAsset?.adAssets
            if (assets && assets.length > 0) {
                if (selectFormatData.format === 'carousel') {
                    const basicAssets = assets.slice(0, 5).map((asset) => {
                        const proxyUrl = constructProxyUrl(asset.url)
                        return {
                            id: asset.id,
                            url: proxyUrl,
                            croppedUrl: proxyUrl,
                            type: getMediaType(asset.name),
                            width: asset.width,
                            height: asset.height,
                            name: asset.name,
                        }
                    })
                    setCarouselAssets(basicAssets)

                    // Fetch detailed data for first asset to get crop info
                    if (basicAssets.length > 0) {
                        fetchAdAsset({ variables: { id: basicAssets[0].id } })
                    }
                } else {
                    // For single image format, get first asset
                    const firstAsset = assets[0]
                    fetchAdAsset({ variables: { id: firstAsset.id } })
                }
            } else {
                console.log('No assets found for campaign')
            }
            setLoading(false)
        },
        onError: (err) => {
            console.error('Campaign Assets failed:', err)
            setError(err)
            setLoading(false)
        },
    })

    // Fetch individual asset with crop data
    const [fetchAdAsset] = useLazyQuery(GET_AD_ASSET_BY_ID, {
        fetchPolicy: 'network-only',
        onCompleted: async (data) => {
            if (data?.AdAsset?.adAsset) {
                const asset = data.AdAsset.adAsset
                const proxyUrl = constructProxyUrl(asset.url)

                const assetInfo = {
                    url: proxyUrl,
                    type: getMediaType(asset.name),
                    width: asset.width,
                    height: asset.height,
                    cropData: asset?.cropData,
                    trimData: asset?.trim_data,
                    thumbnails: asset?.thumbnails,
                }
                setAssetData(assetInfo)

                // Handle cropping
                if (asset.cropData && asset.cropData.length > 0) {
                    const squareCrop = asset.cropData.find((crop) => crop.name === 'square')
                    if (squareCrop) {
                        try {
                            const croppedImage = await getCroppedImg(proxyUrl, squareCrop, 0)
                            setCroppedImageUrl(croppedImage)

                            // Update carousel assets if in carousel mode
                            if (selectFormatData.format === 'carousel' && carouselAssets.length > 0) {
                                const updatedAssets = carouselAssets.map((carouselAsset) =>
                                    carouselAsset.id === asset.id
                                        ? { ...carouselAsset, croppedUrl: croppedImage }
                                        : carouselAsset,
                                )
                                setCarouselAssets(updatedAssets)
                            }
                        } catch (error) {
                            console.error('Cropping failed:', error)
                            setCroppedImageUrl(proxyUrl)
                        }
                    } else {
                        setCroppedImageUrl(proxyUrl)
                    }
                } else {
                    setCroppedImageUrl(proxyUrl)
                }
            }
        },
        onError: (err) => {
            console.error('Asset Data failed:', err)
            setError(err)
        },
    })

    // Load assets when campaign ID is available
    useEffect(() => {
        if (campaignId) {
            setLoading(true)
            fetchCampaignAssets({ variables: { campaignId } })
        }
    }, [campaignId])

    const refetchAssets = useCallback(() => {
        if (campaignId) {
            setLoading(true)
            fetchCampaignAssets({ variables: { campaignId } })
        }
    }, [campaignId, fetchCampaignAssets])

    useEffect(() => {
        fetchAdAsset({ variables: { id: assetId } })
    }, [assetId])

    return {
        assetData,
        croppedImageUrl,
        carouselAssets,
        loading,
        error,
        refetchAssets,
    }
}
